import React, { useState, useEffect, useRef } from 'react';
import { X, Bomb, Gift, Tag, Box, Coins, CheckCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { collection, addDoc, serverTimestamp, getDocs, query, limit } from 'firebase/firestore';
import { db } from '../../services/firebase';

const ENTRY_FEE = 10;

const MULTIPLIERS = [
    1.00, 1.15, 1.32, 1.55, 1.84, 2.25, 2.80, 3.50, 4.50, 6.00, 
    8.00, 11.00, 15.00, 22.00, 35.00, 50.00, 75.00, 120.00, 200.00, 350.00, 
    600.00, 1000.00, 2500.00, 5000.00, 10000.00, 50000.00
];

const PRIZE_TIERS = [
    { threshold: 50.0, type: 'product', label: 'MYSTERY SNEAKER', icon: Box, color: '#39FF14' },
    { threshold: 15.0, type: 'credits', label: '500 CRD BONUS', value: 500, icon: Coins, color: '#a855f7' },
    { threshold: 8.0, type: 'discount', label: '30% OFF VOUCHER', value: 'HYPE30', icon: Tag, color: '#3b82f6' },
    { threshold: 3.0, type: 'discount', label: '15% OFF VOUCHER', value: 'HYPE15', icon: Tag, color: '#eab308' },
    { threshold: 1.5, type: 'shipping', label: 'FREE SHIPPING', value: 'FREESHIP', icon: Gift, color: '#ffffff' },
    { threshold: 1.0, type: 'credits', label: 'MONEY BACK', value: 10, icon: Coins, color: '#a1a1aa' },
];

type TileData = {
    state: 'hidden' | 'safe' | 'mine';
    value?: number;
    isPrize?: boolean;
    prizeIcon?: any;
    prizeColor?: string;
    revealedAfter?: boolean; 
};

// ============================================================================
// 🟢 LIVE WINNERS FEED (Adapted for the right sidebar of Mines)
// ============================================================================
const LiveWinnersFeed = ({ pool }: { pool: any[] }) => {
    const [winners, setWinners] = useState<any[]>([]);

    useEffect(() => {
        if (pool.length === 0) return;
        const generateFakeWin = () => pool[Math.floor(Math.random() * pool.length)];
        
        setWinners([generateFakeWin(), generateFakeWin()]);

        const interval = setInterval(() => {
            setWinners(prev => [generateFakeWin(), ...prev.slice(0, 3)]); 
        }, 4500);

        return () => clearInterval(interval);
    }, [pool]);

    if (pool.length === 0) return null;

    return (
        <div className="flex-1 w-full overflow-y-auto scrollbar-hide mt-4 border-t border-[#2a3a4a] pt-4 relative z-10">
            <h3 className="text-[#39FF14] font-black text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-2 drop-shadow-[0_0_10px_rgba(57,255,20,0.8)]">
                <span className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse shadow-[0_0_10px_rgba(57,255,20,1)]"></span> Live Cashouts
            </h3>
            <div className="flex flex-col gap-2">
                {winners.map((win, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-[#0a0a0a] border border-[#2a3a4a] rounded-xl animate-in slide-in-from-top-4 fade-in duration-500">
                        <div className="w-10 h-10 rounded-lg bg-black border border-[#2a3a4a] overflow-hidden shrink-0 flex items-center justify-center p-1">
                            {win.type === 'product' && win.image ? (
                                <img src={win.image} alt={win.name} className="w-full h-full object-contain" />
                            ) : win.type === 'voucher' ? (
                                <Tag className="text-[#eab308]" size={18} />
                            ) : (
                                <Coins className="text-[#39FF14]" size={18} />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-zinc-500 text-[8px] font-bold uppercase tracking-widest">Anon cashed out</p>
                            <p className="text-white font-black text-[10px] uppercase truncate tracking-tight">
                                {win.brand ? `${win.brand} ` : ''}{win.name}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const HypeMinesModal = ({ isOpen, onClose, profile, updateProfile, onBuyCredits }: any) => {
    const [gameState, setGameState] = useState<'idle' | 'playing' | 'cashed_out' | 'crashed'>('idle');
    const [board, setBoard] = useState<TileData[]>(Array(25).fill({ state: 'hidden' }));
    const [clicks, setClicks] = useState(0);
    const [wonPrize, setWonPrize] = useState<any>(null);
    const [localBalance, setLocalBalance] = useState<number>(0);
    
    // 🟢 FIREBASE DATA POOLS
    const [rewardPool, setRewardPool] = useState<any[]>([]);
    const [displayProduct, setDisplayProduct] = useState<string | null>(null);

    const timelineRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (profile?.cred !== undefined) setLocalBalance(profile.cred);
    }, [profile?.cred]);

    // 🟢 FETCH REAL PRODUCTS ON MOUNT
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const q = query(collection(db, 'products'), limit(15));
                const snapshot = await getDocs(q);
                const products = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        type: 'product',
                        image: data.image || data.images?.[0],
                        name: data.name,
                        brand: data.brand
                    };
                });

                // Combine real products with virtual rewards for the feed
                const mixedPool = [
                    ...products,
                    { type: 'voucher', name: '30% OFF VOUCHER' },
                    { type: 'voucher', name: 'FREE SHIPPING PASS' },
                    { type: 'credits', name: '500 CRD BONUS' },
                    { type: 'credits', name: '100 CRD SECURED' }
                ];
                setRewardPool(mixedPool);

                // Set a random real product image to act as the "Mystery Sneaker" for this session
                const validImages = products.filter(p => p.image).map(p => p.image);
                if (validImages.length > 0) {
                    setDisplayProduct(validImages[Math.floor(Math.random() * validImages.length)]);
                }
            } catch (error) {
                console.error("Failed to load products for Mines:", error);
            }
        };
        fetchProducts();
    }, []);

    const getSafeChance = (clickNum: number) => {
        if (clickNum === 1) return 0.75; 
        if (clickNum <= 3) return 0.85;  
        if (clickNum === 4) return 0.50; 
        if (clickNum <= 7) return 0.60;
        if (clickNum === 8) return 0.35; 
        if (clickNum <= 11) return 0.40;
        if (clickNum === 12) return 0.15; 
        if (clickNum <= 19) return 0.20;
        return 0.05; 
    };

    const startGame = async () => {
        if (localBalance < ENTRY_FEE) return;

        const newBalance = localBalance - ENTRY_FEE;
        setLocalBalance(newBalance);
        updateProfile({ cred: newBalance });

        setBoard(Array(25).fill({ state: 'hidden' }));
        setClicks(0);
        setWonPrize(null);
        setGameState('playing');
        
        if (timelineRef.current) timelineRef.current.scrollLeft = 0;
    };

    const revealBoard = () => {
        setBoard(prev => prev.map((tile, i) => {
            if (tile.state !== 'hidden') return tile;
            const isMine = Math.random() > 0.75;
            if (isMine) return { state: 'mine', revealedAfter: true };
            const randomMult = MULTIPLIERS[Math.floor(Math.random() * 5) + clicks];
            return { state: 'safe', value: randomMult, isPrize: false, revealedAfter: true }; 
        }));
    };

    const handleTileClick = (index: number) => {
        if (gameState !== 'playing' || board[index].state !== 'hidden') return;

        const nextClick = clicks + 1;
        const chance = getSafeChance(nextClick);
        const roll = Math.random();
        const newBoard = [...board];

        if (roll <= chance) {
            const nextMult = MULTIPLIERS[nextClick];
            const currentPrize = PRIZE_TIERS.find(tier => nextMult >= tier.threshold);
            const prevPrize = PRIZE_TIERS.find(tier => MULTIPLIERS[clicks] >= tier.threshold);
            
            let isPrize = false;
            let icon = null;
            let color = '';

            // Highlight the tile if a new prize threshold is crossed
            if (currentPrize && currentPrize !== prevPrize && currentPrize.type !== 'credits') {
                isPrize = true;
                icon = currentPrize.icon;
                color = currentPrize.color;
            }

            newBoard[index] = { 
                state: 'safe', 
                value: nextMult, 
                isPrize, 
                prizeIcon: icon, 
                prizeColor: color 
            };
            
            setBoard(newBoard);
            setClicks(nextClick);
            
            if (timelineRef.current) {
                const scrollAmount = Math.max(0, (nextClick - 2) * 80);
                timelineRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
            }
            
            if (nextClick === 25) handleCashOut(nextClick);
        } else {
            newBoard[index] = { state: 'mine' };
            setBoard(newBoard);
            setGameState('crashed');
            revealBoard();
        }
    };

    // 🟢 UPDATED CASHOUT WITH VAULT & NOTIFICATION FIXES
    const handleCashOut = async (overrideClicks?: number) => {
        if (gameState !== 'playing' || clicks === 0) return;
        
        const finalClicks = overrideClicks || clicks;
        const lockedMultiplier = MULTIPLIERS[finalClicks];
        
        // Find the prize tier they reached
        const prize = PRIZE_TIERS.find(tier => lockedMultiplier >= tier.threshold) || PRIZE_TIERS[PRIZE_TIERS.length - 1];
        const profit = Math.floor(ENTRY_FEE * lockedMultiplier);
        
        setGameState('cashed_out');
        setWonPrize({ ...prize, profit });
        revealBoard();

        // 1. UPDATE BANK BALANCE
        const updatedBalance = localBalance + profit;
        setLocalBalance(updatedBalance);
        
        try { 
            await updateProfile({ cred: updatedBalance }); 

            // 2. 🟢 SECURE PRIZE IN THE VAULT (If it's not just a small credit return)
            // Fixes the TS undefined error by falling back to 0
            const numericalValue = typeof prize.value === 'number' ? prize.value : 0; 

            if (prize.type !== 'credits' || numericalValue > ENTRY_FEE) {
                await addDoc(collection(db, "rewards"), {
                    userId: profile.uid,
                    title: prize.label,
                    rewardType: prize.type, 
                    value: prize.value || lockedMultiplier, 
                    sourceGame: 'Scoop Sweeper',
                    status: 'available',
                    createdAt: serverTimestamp()
                });

                // 3. 🟢 TRIGGER NOTIFICATION
                await addDoc(collection(db, "notifications"), {
                    userId: profile.uid,
                    type: 'reward',
                    title: 'MINEFIELD CLEARED! 💣',
                    message: `Strategic win! You cashed out at ${lockedMultiplier.toFixed(2)}x and secured: ${prize.label}. Claim it in the Vault.`,
                    read: false,
                    createdAt: serverTimestamp()
                });
            }
        } catch(e) { 
            console.error("Fulfillment Error:", e); 
        }
    };

    if (!isOpen) return null;

    const currentMultiplier = MULTIPLIERS[clicks];
    const activePrize = PRIZE_TIERS.find(tier => currentMultiplier >= tier.threshold) || PRIZE_TIERS[PRIZE_TIERS.length - 1];

    return (
        <div className="fixed inset-0 z-[999] bg-black/95 backdrop-blur-md flex items-center justify-center p-2 lg:p-6 font-sans animate-in fade-in duration-200">
            <div className="w-full max-w-6xl bg-black lg:rounded-3xl flex flex-col shadow-[0_0_80px_rgba(0,0,0,0.8)] overflow-hidden h-[95vh] lg:h-auto border border-cyan-900/30">
                
                {/* HEADER */}
                <div className="flex justify-between items-center px-6 py-4 bg-[#111111] border-b border-[#2a3a4a] shrink-0">
                    <div className="flex items-center gap-3">
                        <Bomb className="text-[#39FF14]" size={24} />
                        <h2 className="text-xl font-black uppercase tracking-widest text-white">Hype Mines</h2>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="bg-black border border-[#2a3a4a] rounded-md px-4 py-2 flex items-center gap-2 shadow-inner">
                            <span className="text-[10px] text-zinc-500 font-black uppercase tracking-widest">Bank</span>
                            <span className="font-mono text-white font-bold">{localBalance} CRD</span>
                        </div>
                        <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white hover:bg-red-500 transition-colors bg-black rounded-md border border-[#2a3a4a]"><X size={20}/></button>
                    </div>
                </div>

                <div className="flex-1 flex flex-col overflow-y-auto">
                    
                    <div className="flex flex-col lg:flex-row items-center justify-center gap-8 p-6 lg:p-10 bg-black">
                        
                        {/* 🟢 LEFT: PRIZE INDEX (NOW DISPLAYS REAL PRODUCTS) */}
                        <div className="hidden lg:flex w-56 h-[400px] bg-black border border-[#2a3a4a] rounded-2xl flex-col items-center justify-center p-6 shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#39FF14] to-transparent opacity-50"></div>
                            <span className="text-zinc-500 text-xs font-black uppercase tracking-[0.2em] mb-8">Secured Loot</span>
                            
                            <div className="relative flex flex-col items-center justify-center mb-6 w-full h-32">
                                <div className="absolute inset-0 blur-2xl opacity-40 rounded-full" style={{ backgroundColor: activePrize.color }}></div>
                                
                                {/* If it's a product and we have an image, show the real item. Otherwise, show the icon. */}
                                {activePrize.type === 'product' && displayProduct ? (
                                    <img src={displayProduct} className="w-28 h-28 object-contain drop-shadow-[0_0_15px_rgba(57,255,20,0.4)] relative z-10 animate-in zoom-in" alt="Secured Gear" />
                                ) : (
                                    <activePrize.icon size={80} color={activePrize.color} className="drop-shadow-[0_0_20px_currentColor] relative z-10" />
                                )}
                            </div>
                            
                            <h3 className="text-white font-black text-center uppercase tracking-widest text-sm mb-2">{clicks > 0 ? activePrize.label : "CLEAR TO UNLOCK"}</h3>
                            <span className="text-[#39FF14] font-mono text-3xl drop-shadow-[0_0_10px_rgba(57,255,20,0.5)]">{currentMultiplier.toFixed(2)}x</span>
                        </div>

                        {/* CENTER GRID AREA (Remains pure icons/coins) */}
                        <div className="w-full max-w-[420px] aspect-square relative z-10">
                            
                            {/* OVERLAYS */}
                            {gameState === 'crashed' && (
                                <div className="absolute inset-0 z-30 bg-black/60 flex flex-col items-center justify-center backdrop-blur-sm animate-in zoom-in-95 rounded-2xl">
                                    <div className="bg-[#111111] p-8 rounded-2xl border-2 border-red-500 text-center shadow-[0_0_60px_rgba(220,38,38,0.5)]">
                                        <Bomb size={64} className="text-red-500 mx-auto mb-4 animate-pulse drop-shadow-[0_0_15px_rgba(220,38,38,0.8)]" />
                                        <h2 className="text-3xl font-black text-white uppercase tracking-widest mb-2">You Hit A Mine</h2>
                                        <p className="text-red-400 font-mono">Lost {ENTRY_FEE} CRD</p>
                                    </div>
                                </div>
                            )}

                            {gameState === 'cashed_out' && wonPrize && (
                                <div className="absolute inset-0 z-30 bg-[#39FF14]/10 flex flex-col items-center justify-center backdrop-blur-sm animate-in zoom-in-95 rounded-2xl">
                                    <div className="bg-[#111111] p-8 rounded-2xl border-2 border-[#39FF14] text-center shadow-[0_0_60px_rgba(57,255,20,0.4)] w-[90%]">
                                        <CheckCircle size={64} className="text-[#39FF14] mx-auto mb-4 drop-shadow-[0_0_15px_rgba(57,255,20,0.8)]" />
                                        <p className="text-xs text-zinc-400 tracking-[0.2em] mb-1">Cashed out at {currentMultiplier.toFixed(2)}x</p>
                                        <h2 className="text-2xl font-black text-white uppercase tracking-tighter leading-tight mb-4">{wonPrize.label}</h2>
                                        <div className="bg-black p-4 rounded-xl border border-[#2a3a4a]">
                                            <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Total Payout</p>
                                            <p className="font-mono text-3xl text-[#39FF14] drop-shadow-md">+{wonPrize.profit} CRD</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* GRID */}
                            <div className="grid grid-cols-5 gap-2 lg:gap-3 w-full h-full relative z-10">
                                {board.map((tile, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleTileClick(index)}
                                        disabled={gameState !== 'playing' || tile.state !== 'hidden'}
                                        className={`relative w-full h-full rounded-xl lg:rounded-2xl transition-all duration-200 flex items-center justify-center overflow-hidden outline-none
                                            ${tile.state === 'hidden' 
                                                ? 'bg-[#2f4553] hover:bg-[#3a5463] border-t border-[#466375] border-b-[6px] border-b-[#111111] active:border-b-0 active:translate-y-[6px] cursor-pointer shadow-[0_4px_10px_rgba(0,0,0,0.5)]' 
                                                : tile.state === 'safe'
                                                ? 'bg-[#050505] border-2 border-[#39FF14] shadow-[0_0_25px_rgba(57,255,20,0.4),inset_0_0_20px_rgba(57,255,20,0.2)]' 
                                                : 'bg-[#050505] border-2 border-red-500 shadow-[0_0_25px_rgba(220,38,38,0.5),inset_0_0_20px_rgba(220,38,38,0.3)]' 
                                            }
                                            ${tile.revealedAfter ? 'opacity-40 scale-95 grayscale' : 'opacity-100'}
                                        `}
                                    >
                                        <div className={`w-full h-full flex items-center justify-center transition-all duration-300 ${tile.state === 'hidden' ? 'opacity-0 scale-50' : 'opacity-100 scale-100'}`}>
                                            
                                            {tile.state === 'safe' && !tile.isPrize && (
                                                <div className="relative flex items-center justify-center w-[70%] h-[70%] max-w-[60px] max-h-[60px] rounded-full bg-gradient-to-b from-yellow-300 to-yellow-600 border-[3px] border-yellow-700 shadow-[inset_0_-3px_5px_rgba(0,0,0,0.4),_0_0_20px_rgba(234,179,8,0.6)]">
                                                    <div className="absolute inset-[3px] rounded-full border border-yellow-200/60"></div>
                                                    <span className="text-[10px] lg:text-sm font-black text-yellow-950 tracking-tighter drop-shadow-sm">{tile.value}x</span>
                                                </div>
                                            )}

                                            {tile.state === 'safe' && tile.isPrize && (
                                                <div className="relative flex flex-col items-center justify-center w-full h-full drop-shadow-[0_0_20px_currentColor]" style={{ color: tile.prizeColor }}>
                                                    <tile.prizeIcon size={36} className="mb-1" />
                                                    <span className="text-[9px] lg:text-[11px] font-black bg-black/80 px-2 py-0.5 rounded shadow-lg">{tile.value}x</span>
                                                </div>
                                            )}

                                            {tile.state === 'mine' && (
                                                <Bomb size={44} className="text-red-500 fill-red-500 drop-shadow-[0_0_20px_rgba(220,38,38,1)] animate-pulse" />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* 🟢 RIGHT: DANGER INDEX & LIVE WINNERS */}
                        <div className="hidden lg:flex w-64 h-[400px] bg-black border border-[#2a3a4a] rounded-2xl flex-col items-center justify-start p-5 shadow-xl overflow-hidden">
                            {/* Danger Level (Compact) */}
                            <div className="flex items-center gap-4 border-b border-[#2a3a4a] w-full pb-4">
                                <div className="relative w-12 h-12 flex items-center justify-center shrink-0">
                                    <div className="absolute inset-0 blur-lg opacity-40 rounded-full bg-red-500"></div>
                                    <Bomb size={32} className="text-red-500 fill-red-500 drop-shadow-[0_0_10px_rgba(239,68,68,0.8)] relative z-10 animate-pulse" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-zinc-500 text-[9px] font-black uppercase tracking-widest">Hidden Mines</span>
                                    <span className="text-red-500 font-mono text-xl drop-shadow-[0_0_5px_rgba(239,68,68,0.5)] leading-tight">HIGH</span>
                                </div>
                            </div>

                            {/* Live Feed */}
                            <LiveWinnersFeed pool={rewardPool} />
                        </div>

                    </div>

                    {/* BOTTOM AREA */}
                    <div className="w-full bg-[#111111] border-t border-[#2a3a4a] flex flex-col z-20 shrink-0">
                        
                        {/* TIMELINE AREA */}
                        <div className="w-full border-b border-[#2a3a4a] p-4 flex flex-col gap-2 bg-black">
                            <div className="relative flex items-center max-w-4xl mx-auto w-full">
                                <ChevronLeft size={20} className="text-zinc-600 absolute left-0 z-10 bg-black p-1" />
                                <div ref={timelineRef} className="flex gap-2 lg:gap-4 overflow-x-auto scrollbar-hide px-8 py-2 snap-x w-full">
                                    {MULTIPLIERS.slice(1).map((mult, i) => {
                                        const isCurrent = i + 1 === clicks;
                                        const isPassed = i + 1 < clicks;
                                        return (
                                            <div key={i} className={`flex flex-col items-center justify-center shrink-0 w-16 lg:w-20 h-12 lg:h-14 rounded-lg border snap-center transition-all duration-300 ${
                                                isCurrent ? 'bg-[#2563eb] border-[#3b82f6] text-white shadow-[0_0_20px_rgba(59,130,246,0.6)] scale-110' 
                                                : isPassed ? 'bg-[#050505] border-[#39FF14] text-[#39FF14] shadow-[0_0_10px_rgba(57,255,20,0.2)]' 
                                                : 'bg-[#1a242d] border-[#2a3a4a] text-zinc-600'
                                            }`}>
                                                <span className="font-black text-sm lg:text-base">{mult.toFixed(2)}x</span>
                                                <span className="text-[8px] uppercase tracking-widest opacity-50">{i+1} hits</span>
                                            </div>
                                        )
                                    })}
                                </div>
                                <ChevronRight size={20} className="text-zinc-600 absolute right-0 z-10 bg-black p-1" />
                            </div>
                        </div>

                        {/* CONTROLS */}
                        <div className="p-4 lg:p-6 flex flex-col lg:flex-row gap-4 max-w-4xl mx-auto w-full">
                            
                            <div className="flex-1 bg-black rounded-xl flex items-center justify-between p-4 border border-[#2a3a4a] shadow-inner">
                                <span className="text-xs text-zinc-500 font-black uppercase tracking-widest">Bet Amount</span>
                                <div className="flex items-center gap-2">
                                    <Coins size={18} className="text-zinc-400" />
                                    <span className="font-mono text-white text-xl font-bold">{ENTRY_FEE}.00</span>
                                </div>
                            </div>

                            <div className="flex-[2] h-16 lg:h-20">
                                {gameState === 'idle' || gameState === 'crashed' || gameState === 'cashed_out' ? (
                                    <button 
                                        onClick={startGame} 
                                        disabled={localBalance < ENTRY_FEE}
                                        className="w-full h-full bg-[#39FF14] text-black font-black uppercase tracking-[0.2em] text-xl lg:text-2xl rounded-xl hover:bg-[#32e612] transition-all shadow-[0_0_30px_rgba(57,255,20,0.3)] hover:shadow-[0_0_40px_rgba(57,255,20,0.5)] disabled:opacity-50 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:shadow-none"
                                    >
                                        {localBalance < ENTRY_FEE ? 'Insufficient Funds' : 'BET & START'}
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => handleCashOut()} 
                                        disabled={clicks === 0} 
                                        className="w-full h-full bg-[#ec4899] text-white font-black uppercase tracking-[0.2em] text-2xl lg:text-3xl rounded-xl hover:bg-[#db2777] transition-all shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:shadow-[0_0_60px_rgba(236,72,153,0.7)] disabled:opacity-50 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:shadow-none flex flex-col items-center justify-center leading-none"
                                    >
                                        <span className="mb-1 text-white drop-shadow-md">CASHOUT</span>
                                        {clicks > 0 && <span className="text-sm font-mono tracking-widest text-white/90 drop-shadow-md">{(ENTRY_FEE * currentMultiplier).toFixed(2)} CRD</span>}
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};